// @givmo/assistant-panel — the host's configuration, and the ONE seam a product's own words,
// surfaces and organization enter through.
//
// ══ THE SEAM IS DATA, NOT A DISCRIMINANT (D-EP-44) ═════════════════════════════════════════════
//
// This panel is built ONCE and knows nothing about employers, companies, charities or any other
// entity class. Everything class-specific arrives here as DATA: the words ({@link AssistantVocab}),
// the pages a conversation can be opened from ({@link AssistantSurface}), the organization the
// consent instrument binds (`orgRef`), and the extra `entry_context` keys the wire carries
// (`entryContextExtra`). There is deliberately no `entityClass` field and nothing in this package
// branches on the host's identity — the same rule `@givmo/org-console` keeps, and for the same
// reason: the backend resolves the class from `(user, org_ref)` precisely so the wire never carries
// one, and a client that could name its own class is a client picking its own authorization rules.
//
// `src/panel-generic.test.ts` is the mechanical guard, and it is the only one this package has —
// no app-level copy rail in this repo reaches a `packages/` member.

import type { AssistantAnalyticsEmit, GivmoEnv } from '@givmo/ai';
import type { LucideLikeIcon } from '@givmo/design-system';

/**
 * One page (or family of pages) the panel can be opened from.
 *
 * A surface is the unit the CONTEXT CHIP and the SUGGESTED PROMPTS are scoped to (D-EP-40): the
 * thread never changes, but walking from one surface to another swaps the chip and the prompts and
 * nothing else. The `icon` is the host's OWN glyph for that destination — matching the highlighted
 * navigation row is what makes the chip legible without a "Context:" label.
 */
export interface AssistantSurface {
  /** Stable id the host resolves from its own router. Also the chip's `data-testid` suffix. */
  id: string;
  /** What the chip says — the host's name for the page ("Reports"). */
  label: string;
  /** The host's own glyph for this destination. */
  icon: LucideLikeIcon;
  /** This surface's suggested prompts, in display order. Rendered verbatim, never generated. */
  prompts: readonly string[];
}

/**
 * Every word this package renders. All of it host-supplied; none of it branched on.
 *
 * ⚠ `productName` is the user-facing name of the assistant and appears in three places (the entry
 * control, the panel title, the acceptance card's title template). D-EP-43 fixes it for Givmo's
 * hosts; it lives here because a package that hard-codes a product name is not a package.
 */
export interface AssistantVocab {
  /** The assistant's user-facing name — the entry label and the panel title. */
  productName: string;
  /** Composer placeholder. */
  composerPlaceholder: string;
  /** Empty-state heading. */
  emptyTitle: string;
  /** Empty-state body. */
  emptyBody: string;
  /** Reply action: copy the reply text. */
  copyAction: string;
  /** Reply action: write the reply into the host's registered field. Falls back when the host's
   *  insertion target names its own label. */
  insertAction: string;
  /** The quiet failed-turn line, and the word that retries it. */
  failedLine: string;
  failedRetry: string;
  /** Accessible name for the stop control shown while a reply is streaming. */
  stopLabel: string;
  /** Accessible name for the send control. */
  sendLabel: string;
  /** Acceptance card title. `{org}` is replaced with the organization's name. */
  consentTitle: string;
  /**
   * The line under the server-supplied instrument body saying WHAT the acceptance does. `{org}` is
   * replaced with the organization's name. `null` when the instrument body already says it — the
   * bytes the server serves are the hashed bytes and this line is chrome around them, so a host
   * whose instrument carries its own scope sentence passes `null` rather than printing it twice.
   */
  consentScopeLine: string | null;
  /** Acceptance card buttons, in cancel-then-confirm order. */
  consentDecline: string;
  consentAccept: string;
  /** The line under the instrument body in the per-admin first-use notice. `{org}` is replaced. */
  noticeScopeLine: string;
  /** The notice's dismissal. */
  noticeDismiss: string;
  /**
   * What someone who cannot bind the organization is told when it has not accepted yet. Deliberately
   * one honest line and no control — there is nothing for them to do here.
   */
  consentBlockedLine: string;
  /** What anyone is told when this environment serves no instrument at all (the 404 state). */
  unavailableLine: string;
  /**
   * The phrase inside the instrument body to turn into a link to `privacyPolicyUrl` — the policy's
   * own title as the instrument spells it. `null` for no link.
   *
   * ⚠ It must match the instrument's own wording exactly; a phrase the body does not contain simply
   * produces no link, which is the safe direction. It changes no bytes — see `InstrumentBody`.
   */
  policyLinkPhrase: string | null;
}

/** Everything one mount of the panel needs. */
export interface AssistantPanelConfig {
  /**
   * The organization the consent instrument binds, or `null` when the host has not resolved one yet
   * (the read is skipped and the panel waits rather than guessing). The ONLY organization input.
   */
  orgRef: string | null;
  /** The organization's display name, for the `{org}` slots in {@link AssistantVocab}. */
  orgName: string | null;
  /**
   * Whether the signed-in person may bind the ORGANIZATION to the instrument.
   *
   * ⚠ ADVISORY ONLY, and permissive by design: it decides whether this client offers the acceptance
   * card or the honest "someone else has to" line, never whether the write is allowed. The server
   * is the authority and refuses on its own; a client that gated strictly would strand a real
   * representative behind a slow or failed role read.
   */
  canAcceptForOrg: boolean;
  /**
   * The `entry_context.surface` value the BACKEND recognizes for this host.
   *
   * ⚠ It is a SELECTOR among audiences the caller already holds, never a grant: the server's
   * audience resolution additionally requires the flag pair and the server-injected organization
   * membership, so naming a surface here buys nothing a caller does not already have.
   */
  wireSurface: string;
  /**
   * The surface value stamped on each SEND's `client_context` for the page the host is showing
   * right now — when the backend recognizes more than one surface for this host and holds a reply
   * from one of them to a different set of output limits (its register). Defaults to `wireSurface`.
   *
   * ⚠ CREATION AND THREAD ADOPTION ALWAYS USE `wireSurface`, NEVER THIS. One thread persists across
   * pages (D-EP-40) and its identity is its creation surface; only the per-send stamp moves with the
   * page. Like `entryContextExtra`, supply it FRESH per page: the server takes the send's dict whole,
   * so a stale value keeps a register alive after the page justifying it is gone, and a value the
   * server does not recognize resolves to the consumer assistant, silently. It is a selector among
   * surfaces the caller already holds, never a grant.
   */
  pageWireSurface?: string;
  /**
   * Extra context keys for the page the host is showing RIGHT NOW, alongside `surface`.
   *
   * They are stamped twice, from one vocabulary: onto the conversation's `entry_context` at creation,
   * and onto `client_context` on every send.
   *
   * ⚠ SUPPLY THEM FRESH PER PAGE, AND OMIT WHAT NO LONGER APPLIES. The server takes the send's dict
   * WHOLE — it does not merge with the previous send's — so a host that keeps returning a stale key
   * keeps that scope alive long after the page justifying it is gone, and one that returns nothing
   * on a page with no scope falls the turn back to the thread's creation context. Returning `{}` on
   * such a page is the correct answer, not an omission.
   */
  entryContextExtra?: Readonly<Record<string, string>>;
  /**
   * Entry-context values a thread must ALSO carry to be adopted — a `thread: 'record'` scope's
   * (`assistant-scopes.ts`): one conversation per record in view. Absent, the person's one thread on
   * `wireSurface` is the thread (D-EP-40).
   */
  threadMatch?: Readonly<Record<string, string>>;
  /** The surfaces this host maps its pages to. */
  surfaces: readonly AssistantSurface[];
  /** The id of the surface the host is showing right now. An id not in `surfaces` renders no chip. */
  activeSurfaceId: string;
  /** Words — see {@link AssistantVocab}. */
  vocab: AssistantVocab;
  /** The host's privacy policy, linked from the instrument body when that body names one. */
  privacyPolicyUrl: string;
  /** API origin the SSE `stream_path` resolves against. */
  apiBaseUrl: string;
  /** Auth headers for the SSE connection — resolved per (re)connect so a fresh token is minted. */
  authHeaders: () => Promise<Record<string, string>> | Record<string, string>;
  /** Selects the deep-link host for any `action_chips` a reply carries. */
  env: GivmoEnv;
  /** Structural analytics emit (never chat text). Optional. */
  emit?: AssistantAnalyticsEmit;
  /**
   * Whether to render the server-generated opening message (the conversation's `seq 0`).
   *
   * ⚠ THIS IS A DISCLOSURE DECISION, so it is explicit rather than defaulted. The backend seeds every
   * conversation with a signed consumer greeting that names the assistant as an AI and points at a
   * "Talk to a human" control. On a surface governed by D-EP-37 (no AI-authorship labels on replies)
   * and D-EP-38 (no controls beyond insert/copy) that greeting is both a label the ruling forbids
   * and a pointer to a control that does not exist — and the surface carries its OWN disclosure in
   * the acceptance instrument and the first-use notice. A host in that position passes `false`; a
   * host whose surface relies on the greeting as its disclosure passes `true`. The message is
   * persisted server-side either way.
   */
  renderServerOpeningMessage: boolean;
  /**
   * Where the per-admin first-use notice records its dismissal.
   *
   * Per-admin client state by design (the backend records the ORGANIZATION's acceptance; whether one
   * admin has seen the notice is not an organization fact), so it is `localStorage`-class. Key it
   * per identity — a shared browser must not let one admin's dismissal hide the notice from the next.
   */
  noticeStorageKey: string | null;
  /** Test-id namespace, so two mounts on one page cannot collide. Defaults to `assistant`. */
  testIdPrefix?: string;
}

/** The default test-id namespace. Part of the test-id contract — treat a change like an API change. */
export const DEFAULT_TEST_ID_PREFIX = 'assistant';

/** This mount's test-id namespace. */
export function testIdPrefix(config: Pick<AssistantPanelConfig, 'testIdPrefix'>): string {
  const prefix = (config.testIdPrefix ?? '').trim();
  return prefix.length > 0 ? prefix : DEFAULT_TEST_ID_PREFIX;
}

/**
 * The surface the host is showing, or `null` when its id names none.
 *
 * `null` is a real answer rather than a fallback to the first surface: the chip states which page
 * the assistant is reading, so showing the wrong page's name is worse than showing none.
 */
export function activeSurface(config: AssistantPanelConfig): AssistantSurface | null {
  return config.surfaces.find((surface) => surface.id === config.activeSurfaceId) ?? null;
}

/**
 * Substitute the organization's name into a `{org}` slot.
 *
 * An unresolved name falls back to the generic phrase rather than printing an empty gap or the
 * literal placeholder — "Turn on X for" is worse than "for your organization".
 *
 * ⚠ The fallback is CAPITALIZED when the slot opens the sentence, because one of the templates does
 * exactly that ("{org} turned this on."). Substituting blindly renders "your organization turned
 * this on." — which reads as a typo and is the first thing a reader notices.
 */
export function withOrgName(template: string, orgName: string | null): string {
  const name = (orgName ?? '').trim();
  if (name.length > 0) return template.replaceAll('{org}', name);
  return template.replace(/^\{org\}/, 'Your organization').replaceAll('{org}', 'your organization');
}
